<!-- NearestLocationCard.vue -->
<template>
  <div class="delivery-tile nearest-location-card">
    <div class="delivery-tile-content">
      <div class="action-card-header">
        <span class="material-icons mr-2">location_city</span>
        <h4 class="action-card-title">Entreprise liée à la commande</h4>
      </div>
      <div class="delivery-tile-body">
        <!-- En-tête avec nom de l'entreprise -->
        <div class="nearest-location-header">
          <h5 class="nearest-location-name">{{ nearestLocation.name }}</h5>
          <Badge v-if="distanceText" :value="distanceText" severity="info" class="distance-badge" />
        </div>

        <!-- Adresse -->
        <div class="info-section">
          <div class="section-header">
            <span class="material-icons text-blue-500">location_on</span>
            <h6 class="section-title">Adresse</h6>
          </div>
          <p class="info-text">{{ nearestLocation.address }}</p>
          <p class="info-text">{{ nearestLocation.zip_code }} {{ nearestLocation.city }}</p>
        </div>

        <!-- Contact -->
        <div class="info-section">
          <div class="section-header">
            <span class="material-icons text-blue-500">person</span>
            <h6 class="section-title">Contact</h6>
          </div>
          <p class="info-text">{{ nearestLocation.first_name }} {{ nearestLocation.last_name }}</p>
          <p class="info-text phone-number" v-if="nearestLocation.telephone">
            <span class="material-icons phone-icon">phone</span>
            <a :href="`tel:${cleanPhoneNumber}`">{{ nearestLocation.telephone }}</a>
          </p>
          <p class="info-text email" v-if="nearestLocation.emails && nearestLocation.emails.length > 0">
            <span class="material-icons email-icon">email</span>
            <a :href="`mailto:${nearestLocation.emails[0]}`">{{ nearestLocation.emails[0] }}</a>
          </p>
        </div>

        <!-- Instructions de livraison -->
        <div class="info-section" v-if="nearestLocation.delivery_instructions">
          <div class="section-header">
            <span class="material-icons text-blue-500">info</span>
            <h6 class="section-title">Instructions</h6>
          </div>
          <p class="info-text whitespace-pre-line">{{ nearestLocation.delivery_instructions }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import Badge from 'primevue/badge';
import Button from 'primevue/button';

const props = defineProps({
  nearestLocation: {
    type: Object,
    required: true
  },
  shopLocation: {
    type: Object,
    default: null
  }
});

// Nettoyage du numéro de téléphone pour l'appel
const cleanPhoneNumber = computed(() => {
  if (!props.nearestLocation.telephone) return '';
  return props.nearestLocation.telephone.replace(/\s+/g, '');
});

// Calcul de la distance entre les deux points (en mètres)
const distance = computed(() => {
  if (!props.shopLocation?.latitude || !props.shopLocation?.longitude || 
      !props.nearestLocation?.latitude || !props.nearestLocation?.longitude) {
    return null;
  }
  
  const R = 6371e3; // Rayon de la terre en mètres
  const φ1 = props.shopLocation.latitude * Math.PI/180;
  const φ2 = props.nearestLocation.latitude * Math.PI/180;
  const Δφ = (props.nearestLocation.latitude - props.shopLocation.latitude) * Math.PI/180;
  const Δλ = (props.nearestLocation.longitude - props.shopLocation.longitude) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // en mètres
});

// Formatage de la distance
const distanceText = computed(() => {
  if (distance.value === null) return null;
  
  if (distance.value < 1000) {
    return `${Math.round(distance.value)}m`;
  } else {
    return `${(distance.value / 1000).toFixed(1)}km`;
  }
});

// Ouvrir l'itinéraire dans une application de navigation
const openNavigation = () => {
  if (!props.nearestLocation.latitude || !props.nearestLocation.longitude) {
    return;
  }
  
  const lat = props.nearestLocation.latitude;
  const lng = props.nearestLocation.longitude;
  const address = encodeURIComponent(props.nearestLocation.address);
  
  // Détecter si c'est un appareil mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    // Pour appareils mobiles, tenter d'ouvrir dans une app native
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      // iOS: préférer Apple Maps
      window.location.href = `https://maps.apple.com/?daddr=${lat},${lng}`;
    } else {
      // Android ou autres: préférer Google Maps
      window.location.href = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
  } else {
    // Pour desktop, ouvrir Google Maps dans un nouvel onglet
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.delivery-tile {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  overflow: hidden;
  width: 100%;
}

.delivery-tile-content {
  display: flex;
  flex-direction: column;
}

.action-card-header {
  padding: 0.8rem 1rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
}

.action-card-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color, #495057);
}

.delivery-tile-body {
  padding: 1rem;
}

.nearest-location-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.nearest-location-name {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e40af;
}

.distance-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.info-section {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;
}

.info-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
}

.info-text {
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0.25rem 0;
  color: var(--text-color, #495057);
}

.phone-number, .email {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.phone-icon, .email-icon {
  font-size: 1rem !important;
  color: #64748b;
}

.material-icons {
  font-size: 1.25rem;
}

.whitespace-pre-line {
  white-space: pre-line;
}

.button-container {
  margin-top: 1rem;
}

@media (min-width: 640px) {
  .action-card-header {
    padding: 1rem;
  }
  
  .nearest-location-name {
    font-size: 1.2rem;
  }
  
  .section-title {
    font-size: 1rem;
  }
  
  .info-text {
    font-size: 1rem;
  }
  
  .material-icons {
    font-size: 1.5rem;
  }
}
</style> 